/**
 * Building Screen Error Handling Utilities
 * Converts Firebase errors and other errors into user-friendly messages
 * SECURITY: Prevents information disclosure while providing helpful feedback
 */

/**
 * Maps Firebase and network errors to user-friendly messages
 * @param {Error} error - The error object from Firebase or async operation
 * @returns {string} User-friendly error message
 */
export const getBuildingScreenErrorMessage = (error) => {
  if (!error) {
    return "An unexpected error occurred. Please try again.";
  }

  const code = error.code || error.message;

  // Network errors
  if (code === "auth/network-request-failed" || code?.includes("network")) {
    return "Network connection lost. Please check your internet and try again.";
  }

  // Firebase auth errors
  if (code === "permission-denied") {
    return "You don't have permission to view this building. Contact your manager.";
  }

  if (code === "not-found") {
    return "This building could not be found. It may have been deleted.";
  }

  // Firestore errors
  if (code === "RESOURCE_EXHAUSTED") {
    return "Too many requests. Please wait a moment and try again.";
  }

  if (code === "DEADLINE_EXCEEDED") {
    return "Request timed out. Please check your connection and try again.";
  }

  // Default
  if (error.message) {
    return error.message;
  }

  return "Failed to load building data. Please try again.";
};

/**
 * Maps form validation errors to user-friendly messages
 * @param {string} formType - Type of form being validated
 * @param {Object} formData - The form data object
 * @returns {string|null} Error message or null if valid
 */
export const validateFormData = (formType, formData) => {
  if (!formData) {
    return `${formType} form is required`;
  }

  // Check if form has required properties based on type
  if (formType === "Weather" || formType === "Weather Report") {
    if (!formData.weatherCondition) {
      return "Weather condition not recorded. Please complete the form.";
    }
  }

  if (formType === "Equipment" || formType === "Access Equipment") {
    if (!formData.equipmentChecked && formData.equipmentChecked !== false) {
      return "Equipment checklist incomplete. Please complete the form.";
    }
  }

  return null;
};

/**
 * Validates that a form is from today
 * @param {Object} form - Form data with date property
 * @returns {boolean} True if form is from today
 */
export const isTodaysForm = (form) => {
  if (!form?.date) return false;

  try {
    // Handle both Firestore Timestamp and Date objects
    const formDate = form.date.toDate 
      ? new Date(form.date.toDate())
      : new Date(form.date);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const fDate = new Date(formDate);
    fDate.setHours(0, 0, 0, 0);

    return fDate.getTime() === today.getTime();
  } catch (error) {
    console.warn("Error validating form date:", error);
    return false;
  }
};

/**
 * Validates building data structure
 * @param {Object} data - Building data from Firebase
 * @returns {Object} Validated data with defaults for missing values
 * @throws {Error} If critical data is missing
 */
export const validateBuildingData = (data) => {
  if (!data) {
    throw new Error("Building data is missing");
  }

  // Validate critical fields
  if (!data.name && !data.site_name) {
    throw new Error("Building name is missing");
  }

  // Provide safe defaults for optional fields
  const validated = {
    ...data,
    address: data.address || "Address not available",
    noDrops: Number(data.noDrops) || 0,
    currentClean: data.currentClean || null,
    lastCleaned: data.lastCleaned || null,
    lastDropCleaned: data.lastDropCleaned || "Unknown",
    currentCleanPercentageCompleted: Number(data.currentCleanPercentageCompleted) || 0,
    weatherForm: data.weatherForm || null,
    equipmentForm: data.equipmentForm || null,
    ppeForm: data.ppeForm || null,
  };

  // Ensure percentage is between 0 and 1
  if (validated.currentCleanPercentageCompleted > 1) {
    validated.currentCleanPercentageCompleted = 1;
  }
  if (validated.currentCleanPercentageCompleted < 0) {
    validated.currentCleanPercentageCompleted = 0;
  }

  return validated;
};
